<script lang="ts" setup>
  import type { ICloseArgs } from '#shared/types/modal.types';
  import type { FormError, FormSubmitEvent } from '@nuxt/ui';
  import { LazyModalForgotPassword, LazyModalInvalidCredentials, LazyModalSignUp } from '#components';
  import { rules } from '#shared/validation';
  import { useAppToast } from '~/composables/shared/useAppToast';
  import SupportChat, { ZENDESK_LO_USER_ID } from '~/utils/services/support-chat.service';

  interface ICredentialsData {
    email: string;
    password: string;
  }

  type TValidationErrorMap = {
    [K in keyof ICredentialsData]?: boolean;
  };

  const emit = defineEmits<{ close: [ICloseArgs] }>();

  const { windowWidth } = useDeviceType();
  const { orientation } = useOrientation();

  const useFullscreen = computed(() => windowWidth.value < 480 && orientation.value === EOrientation.PORTRAIT);

  const requestIsPending = ref(false);

  const { showErrorToast } = useAppToast();
  const validationErrorsMap = ref<TValidationErrorMap>({});

  const state = reactive<ICredentialsData>({
    email: '',
    password: '',
  });

  watchEffect(() => {
    state.email = state.email?.toLowerCase() || '';
  });

  const showPassword = ref(false);

  async function validate(): Promise<FormError[]> {
    const errors: FormError[] = [];

    const testEmail = rules.email.safeParse(state.email);

    validationErrorsMap.value = {};

    if (testEmail.error) {
      errors.push({ name: 'email', message: testEmail.error.issues[0]?.message || '' });
      validationErrorsMap.value.email = true;
    }

    if (!state.password.length) {
      errors.push({ name: 'password', message: 'This field is required' });
      validationErrorsMap.value.password = true;
    }

    return errors;
  }

  async function onSubmit(event: FormSubmitEvent<ICredentialsData>) {
    const { email, password } = event.data;
    const auth = useTypedNuxtApp().$firebase.auth;

    try {
      requestIsPending.value = true;

      await auth?.signInWithEmailAndPassword(email, password);
    } catch (error: any) {
      requestIsPending.value = false;

      console.error(error);

      if (error?.code === 'auth/internal-error' && error?.message) {
        const errorData = JSON.parse(error.message);
        const message = errorData?.error?.message;

        if (message === 'INVALID_LOGIN_CREDENTIALS') {
          await stores.modals.open('invalid-credentials', LazyModalInvalidCredentials);
        } else {
          showErrorToast(message || 'Internal error');
        }
      } else {
        showErrorToast(error.message || 'Internal error');
      }
    }
  }

  async function signInWithGoogle() {
    const { $firebase } = useTypedNuxtApp();

    try {
      requestIsPending.value = true;

      stores.user.setSocialSignInIsPending(true);
      const provider = new $firebase.instance.auth.GoogleAuthProvider();
      await $firebase.auth?.signInWithPopup(provider);

      emit('close', {});
      await navigateTo({ name: ERouteName.LOBBY });
    } catch (error: any) {
      requestIsPending.value = false;
      console.error(error);
      showErrorToast(error.message);
    } finally {
      stores.user.setSocialSignInIsPending(false);
    }
  }

  async function openSignUpModal() {
    await stores.modals.open('sign-up', LazyModalSignUp);
    stores.modals.close('sign-in');
  }

  function openForgotPasswordModal() {
    emit('close', {
      openNext: {
        name: 'forgot-password',
        modalRef: LazyModalForgotPassword,
      },
    });
  }

  onMounted(() => {
    requestIsPending.value = false;
    SupportChat.hideButton();
  });

  onBeforeUnmount(() => {
    if (stores.user.isLoggedIn) return;

    if (SupportChat.getIsInitialized()) {
      SupportChat.toggle();
    } else {
      SupportChat.init(ZENDESK_LO_USER_ID)
        .then(() => SupportChat.toggle())
        .catch((error: Error) => {
          console.error('ZenDesk LO Init error', error);
        });
    }
  });
</script>

<template>
  <UModal
    title="Login"
    description="Login to your account"
    :close="{ onClick: () => emit('close', {}) }"
    :ui="{ content: 'overflow-y-hidden max-w-[460px] w-full' }"
    :dismissible="!requestIsPending"
    :fullscreen="useFullscreen"
  >
    <template #content>
      <div class="overflow-y-scroll">
        <Icon
          class="size-6 absolute text-white top-2.5 right-2.5 cursor-pointer rotate-45"
          name="i-lucide:plus"
          @click="emit('close', {})"
        />
        <div class="flex flex-col items-center justify-start gap-6 px-7 py-6">
          <h2 class="text-white text-center font-barlow text-2xl font-semibold leading-[1.2]">Login</h2>
          <CoreButton
            :loading="stores.user.socialSignInIsPending"
            class="w-full bg-white hover:bg-white/90 active:bg-white/80 disabled:bg-white/80 flex items-center justify-center gap-2.5 h-11"
            @click="signInWithGoogle"
          >
            <template #default>
              <SvgoGoogle class="m-0 mb-[-1px] size-6" />
              <span class="text-[#101317]! font-barlow text-lg font-semibold leading-[16px]">Sign In with Google</span>
            </template>
          </CoreButton>

          <div class="w-full flex items-center justify-center gap-2.5">
            <div class="basis-full h-[1px] w-full bg-white/30" />
            <span
              class="basis-full text-white/70 text-center font-barlow text-sm font-normal leading-[12px] translate-y-[-1px]"
            >
              or login with email
            </span>
            <div class="basis-full h-[1px] w-full bg-white/30" />
          </div>

          <UForm :state="state" :validate="validate" class="w-full flex flex-col gap-4" @submit="onSubmit">
            <UFormField
              :ui="{
                label: 'text-white font-barlow text-sm font-medium leading-snug pb-1',
                error: 'hidden',
              }"
              label="Email"
              name="email"
              required
              eager-validation
            >
              <CoreInput
                v-model="state.email"
                class="form-input"
                variant="subtle"
                :is-invalid="!!validationErrorsMap?.email"
              />
            </UFormField>

            <UFormField
              :ui="{
                label: 'text-white font-barlow text-sm font-medium leading-snug pb-1',
                error: 'hidden',
              }"
              label="Password"
              name="password"
              required
              eager-validation
            >
              <CoreInput
                v-model="state.password"
                :type="showPassword ? 'text' : 'password'"
                class="form-input"
                variant="subtle"
              >
                <template #trailing>
                  <UButton
                    :icon="showPassword ? 'i-hugeicons:view-off-slash' : 'i-hugeicons:view'"
                    aria-label="Toggle password visibility"
                    class="text-[#101317] cursor-pointer"
                    color="neutral"
                    size="xl"
                    variant="link"
                    @click="showPassword = !showPassword"
                  />
                </template>
              </CoreInput>
            </UFormField>

            <CoreButton class="justify-end mt-[-10px] p-0 md:p-0" variant="link" @click="openForgotPasswordModal">
              <template #default>
                <span class="font-barlow text-base font-medium text-[#2196F3] underline cursor-pointer">
                  Forgot Password?
                </span>
              </template>
            </CoreButton>

            <CoreButton
              loading-auto
              class="mt-2"
              size="md"
              type="submit"
              :aria-disabled="requestIsPending"
              :disabled="requestIsPending"
              :class="requestIsPending ? 'grayscale-100' : ''"
            >
              Login
            </CoreButton>
          </UForm>

          <p class="m-0 p-0 text-white font-barlow text-base font-normal leading-[1.5]">
            Don't have an account?
            <span class="text-[#0080FF] font-medium underline cursor-pointer" @click="openSignUpModal">
              Sign Up here
            </span>
          </p>
        </div>
      </div>
    </template>
  </UModal>
</template>
